'use client'

import { useEffect, useMemo, useState } from 'react'
import { horseApi } from '../api/endpoints'
import LuckyIcon from '../components/lucky/LuckyIcon'
import { TrophyGlyph } from '../components/lucky/ProfileGlyphs'
import { adFailMessage, preloadRewardedAd, showRewardedAd } from '../lib/ads'
import { HorseHistoryDetail, HorseHistoryList } from '../components/horse/HorseHistory'
import {
  HorsePollMode,
  HorsePollRound,
  HorsePollEntry,
  HorseHistoryItem,
  HorseEntryPayment,
  HorseEntryPaymentStatus,
  UserHorseLeagueScore,
  Horse,
} from '../types'
import { useLocale, useT } from '../contexts/LocaleContext'
import { LOCALE_META } from '../i18n'

const MODE_IDS: HorsePollMode[] = ['league', 'three_in_four', 'twins', 'three_one', 'more_chance']

/** Backend defaultRoundTitle() strings — localize these; keep custom admin titles. */
const DEFAULT_ROUND_TITLES = new Set([
  'ورس لیگ',
  'هورس لیگ',
  'سه در چهار',
  'دو قلو',
  'سه تا یکی',
  'شانس بیشتر',
  'نظرسنجی اسب',
])

function picksNeeded(mode: HorsePollMode): number {
  switch (mode) {
    case 'league':
      return 1
    case 'twins':
    case 'more_chance':
      return 2
    case 'three_one':
      return 3
    default:
      return 0
  }
}

function roundDisplayTitle(
  r: Pick<HorsePollRound, 'title' | 'mode'>,
  t: (key: string, vars?: Record<string, string | number | boolean | null | undefined>) => string,
): string {
  const title = (r.title || '').trim()
  if (!title || DEFAULT_ROUND_TITLES.has(title)) {
    return t(`horse.mode.${r.mode}.label`)
  }
  return title
}

function prizeTypeLabel(
  type: string | undefined | null,
  t: (key: string, vars?: Record<string, string | number | boolean | null | undefined>) => string,
): string {
  if (!type) return ''
  const key = `coins.${type}`
  const label = t(key)
  return label === key ? type : label
}

export default function HorsePage() {
  const t = useT()
  const { locale } = useLocale()
  const numberLocale = LOCALE_META[locale].numberLocale
  const modes = MODE_IDS.map((id) => ({
    id,
    label: t(`horse.mode.${id}.label`),
    fee: t(`horse.mode.${id}.fee`),
    hint: t(`horse.mode.${id}.hint`),
  }))
  const [mode, setMode] = useState<HorsePollMode>('league')
  const [rounds, setRounds] = useState<HorsePollRound[]>([])
  const [round, setRound] = useState<HorsePollRound | null>(null)
  const [entry, setEntry] = useState<HorsePollEntry | null>(null)
  const [leaderboard, setLeaderboard] = useState<HorsePollEntry[]>([])
  const [leagueBoard, setLeagueBoard] = useState<UserHorseLeagueScore[]>([])
  const [myScore, setMyScore] = useState<UserHorseLeagueScore | null>(null)
  const [selections, setSelections] = useState<Record<string, number[]>>({})
  const [loading, setLoading] = useState(false)
  const [watchingAd, setWatchingAd] = useState(false)
  const [payment, setPayment] = useState<HorseEntryPaymentStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [history, setHistory] = useState<HorseHistoryItem[]>([])
  const [historyItem, setHistoryItem] = useState<HorseHistoryItem | null>(null)

  const loadRounds = async (m: HorsePollMode) => {
    setLoading(true)
    setError(null)
    try {
      const res = await horseApi.listRounds(m, true)
      setRounds(res.rounds || [])
      setRound(null)
      setEntry(null)
      setSelections({})
      if (m === 'league') {
        const [lb, mine] = await Promise.all([
          horseApi.leagueLeaderboard(undefined, 10),
          horseApi.myLeagueScore(),
        ])
        setLeagueBoard(lb.leaderboard || [])
        setMyScore(mine.score)
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || t('horse.errLoad'))
    } finally {
      setLoading(false)
    }
  }

  const loadHistory = async () => {
    try {
      const res = await horseApi.myHistory()
      setHistory(res.items || [])
    } catch {
      // Keep the last list if the request fails.
    }
  }

  useEffect(() => {
    loadRounds(mode)
  }, [mode])

  useEffect(() => {
    void loadHistory()
  }, [])

  const openRound = async (r: HorsePollRound) => {
    setLoading(true)
    setError(null)
    setMessage(null)
    setPayment(null)
    try {
      const [full, mine, board, pay] = await Promise.all([
        horseApi.getRound(r.id),
        horseApi.myEntry(r.id),
        horseApi.roundLeaderboard(r.id, 10),
        horseApi.entryPayment(r.id),
      ])
      setRound(full.round)
      setEntry(mine.entry)
      setLeaderboard(board.entries || [])
      setPayment(pay)
      if (mine.entry?.selections) {
        setSelections(mine.entry.selections)
      } else if (full.round.mode === 'three_in_four') {
        const initial: Record<string, number[]> = {}
        for (const rc of full.round.courses || []) {
          const ids = (full.round.locked_horses || [])
            .filter((lh) => lh.course_id === rc.course_id)
            .map((lh) => lh.horse_id)
          if (ids.length === 4) initial[String(rc.course_id)] = ids
        }
        setSelections(initial)
      } else {
        setSelections({})
      }
      if ((full.round.entry_ads_required || 0) > 0 && !mine.entry) {
        void preloadRewardedAd()
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || t('horse.errGeneric'))
    } finally {
      setLoading(false)
    }
  }

  const need = picksNeeded(mode)

  const togglePick = (courseId: number, horseId: number) => {
    if (entry) return
    const key = String(courseId)
    setSelections((prev) => {
      const cur = prev[key] || []
      if (cur.includes(horseId)) {
        return { ...prev, [key]: cur.filter((id) => id !== horseId) }
      }
      if (cur.length >= need) {
        return { ...prev, [key]: [...cur.slice(1), horseId] }
      }
      return { ...prev, [key]: [...cur, horseId] }
    })
  }

  /** Put horse into place 1..4 within the course (swap with whoever is there). */
  const setThreeInFourPlace = (courseId: number, horseId: number, place: number) => {
    if (entry || place < 1 || place > 4) return
    const key = String(courseId)
    setSelections((prev) => {
      const cur = [...(prev[key] || [])]
      if (cur.length !== 4) return prev
      const from = cur.indexOf(horseId)
      const to = place - 1
      if (from < 0 || from === to) return prev
      ;[cur[from], cur[to]] = [cur[to], cur[from]]
      return { ...prev, [key]: cur }
    })
  }

  const votingOpen = Boolean(round?.voting_open)

  const canSubmit = useMemo(() => {
    if (!round || entry || !votingOpen) return false
    if (round.mode === 'three_in_four') {
      const courses = round.courses || []
      if (courses.length !== 4) return false
      return courses.every((rc) => {
        const sel = selections[String(rc.course_id)] || []
        if (sel.length !== 4) return false
        const locked = new Set(
          (round.locked_horses || [])
            .filter((lh) => lh.course_id === rc.course_id)
            .map((lh) => lh.horse_id),
        )
        if (locked.size !== 4) return false
        const seen = new Set<number>()
        for (const id of sel) {
          if (!locked.has(id) || seen.has(id)) return false
          seen.add(id)
        }
        return true
      })
    }
    const courses = round.courses || []
    return courses.every((rc) => (selections[String(rc.course_id)] || []).length === need)
  }, [round, entry, selections, need, votingOpen])

  const adsRequired = payment?.entry_ads_required ?? round?.entry_ads_required ?? 0
  const feeBronze = payment?.entry_fee_bronze ?? round?.entry_fee_bronze ?? 0
  const adsWatched = payment?.ads_watched ?? 0
  const canPayWithAds = Boolean(payment?.can_pay_with_ads)

  const handleSubmit = async (method: HorseEntryPayment) => {
    if (!round || !canSubmit) return
    if (feeBronze > 0 && method === 'ads' && !canPayWithAds) {
      setError(t('horse.errWatchAdsFirst', { n: adsRequired }))
      return
    }
    setLoading(true)
    setError(null)
    setMessage(null)
    try {
      const res = await horseApi.submit(round.id, selections, feeBronze > 0 ? method : undefined)
      setEntry(res.entry)
      setMessage(t('horse.msgSubmitted'))
      const [board, pay] = await Promise.all([
        horseApi.roundLeaderboard(round.id, 10),
        horseApi.entryPayment(round.id),
      ])
      setLeaderboard(board.entries || [])
      setPayment(pay)
      void loadHistory()
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || t('horse.errNotSubmitted'))
    } finally {
      setLoading(false)
    }
  }

  const handleWatchAd = async () => {
    if (!round || entry || watchingAd || adsRequired <= 0) return
    if (adsWatched >= adsRequired) return
    setWatchingAd(true)
    setError(null)
    try {
      const ad = await showRewardedAd()
      if (!ad.ok) {
        setError(adFailMessage(ad.reason))
        return
      }
      const pay = await horseApi.watchAd(round.id)
      setPayment(pay)
      setMessage(t('horse.msgAdProgress', { watched: pay.ads_watched, required: pay.entry_ads_required }))
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || t('horse.errAdFailed'))
    } finally {
      setWatchingAd(false)
    }
  }

  const activeMode = modes.find((m) => m.id === mode)

  const renderHorseChip = (horse: Horse, selected: boolean, onClick: () => void) => (
    <button
      key={horse.id}
      type="button"
      disabled={!!entry || !votingOpen}
      onClick={onClick}
      className={`lucky-horse__chip ${selected ? 'is-on' : ''}`}
    >
      {horse.photo_url && <img src={horse.photo_url} alt="" className="lucky-horse__photo" />}
      <strong>#{horse.number}</strong>
      <span>{horse.name}</span>
      {horse.finish_position != null && (
        <em>{t('horse.finishPosition', { n: horse.finish_position })}</em>
      )}
    </button>
  )

  return (
    <div className="lucky-horse mini-app-container pb-20 min-h-screen">
      <div className="lucky-horse__inner">
        <header className="lucky-horse__header">
          <div className="lucky-horse__header-icon">
            <TrophyGlyph size={32} />
          </div>
          <div>
            <h1>{t('horse.title')}</h1>
            <p>{t('horse.subtitle')}</p>
          </div>
        </header>

        <div className="lucky-horse__modes">
          {modes.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setMode(m.id)}
              className={`lucky-horse__mode ${mode === m.id ? 'is-on' : ''}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        <section className="lucky-horse__card">
          <p className="lucky-horse__hint">
            {activeMode?.hint}
            <span>{t('horse.entryPrefix')}{activeMode?.fee}</span>
          </p>
        </section>

        {error && <div className="lucky-horse__alert lucky-horse__alert--err">{error}</div>}
        {message && <div className="lucky-horse__alert lucky-horse__alert--ok">{message}</div>}
        {loading && <div className="lucky-horse__empty">{t('common.loading')}</div>}

        {mode === 'league' && (
          <section className="lucky-horse__card">
            <h2 className="lucky-horse__section-title">
              <TrophyGlyph size={20} />
              {t('horse.monthlyTop')}
            </h2>
            {myScore && (
              <p className="lucky-horse__my-score">
                {t('horse.yourScore')} <strong>{myScore.total_score}</strong>
                <span> {t('horse.todayScore', { n: myScore.daily_score })}</span>
              </p>
            )}
            <div className="lucky-horse__board">
              {leagueBoard.map((row, i) => (
                <div key={row.user_id} className="lucky-horse__board-row">
                  <span>
                    {i + 1}. {row.user?.first_name || row.user?.username || `#${row.user_id}`}
                  </span>
                  <strong>{row.total_score}</strong>
                </div>
              ))}
              {leagueBoard.length === 0 && (
                <p className="lucky-horse__empty">{t('horse.emptyNoScores')}</p>
              )}
            </div>
          </section>
        )}

        {!round && historyItem && (
          <HorseHistoryDetail
            item={historyItem}
            numberLocale={numberLocale}
            onBack={() => setHistoryItem(null)}
            onOpenRound={(r) => {
              setHistoryItem(null)
              void openRound(r)
            }}
          />
        )}

        {!round && !historyItem && (
          <div className="lucky-horse__list">
            <h2 className="lucky-horse__list-title">{t('horse.openPollsTitle')}</h2>
            {rounds.length === 0 && !loading && (
              <div className="lucky-horse__empty">{t('horse.emptyNoPolls')}</div>
            )}
            {rounds.map((r) => (
              <article key={r.id} className="lucky-horse__round">
                <div>
                  <h3>{roundDisplayTitle(r, t)}</h3>
                  <p>
                    {r.entry_fee_bronze > 0
                      ? t('horse.roundEntryPaid', {
                          bronze: r.entry_fee_bronze,
                          ads: r.entry_ads_required || (r.mode === 'more_chance' ? 3 : 5),
                        })
                      : t('common.free')}
                    {t('horse.courseCount', { n: r.courses?.length || 0 })}
                  </p>
                </div>
                <button type="button" className="lucky-horse__join" onClick={() => openRound(r)}>
                  {t('horse.join')}
                </button>
              </article>
            ))}
          </div>
        )}

        {!round && !historyItem && (
          <HorseHistoryList items={history} numberLocale={numberLocale} onOpen={setHistoryItem} />
        )}

        {round && (
          <div className="lucky-horse__detail">
            <div className="lucky-horse__detail-top">
              <h2>{roundDisplayTitle(round, t)}</h2>
              <button type="button" className="lucky-horse__back" onClick={() => setRound(null)}>
                {t('common.back')}
              </button>
            </div>

            {entry && (
              <div className="lucky-horse__alert lucky-horse__alert--ok">
                {t('horse.entrySubmitted', { score: entry.score })}
                {entry.is_winner &&
                  t('horse.entryWinner', {
                    amount: entry.prize_amount,
                    type: prizeTypeLabel(entry.prize_type, t),
                  })}
              </div>
            )}

            {round.mode === 'three_in_four' ? (
              <>
                {(round.courses || []).map((rc) => {
                  const course = rc.course
                  if (!course) return null
                  const orderIds = selections[String(course.id)] || []
                  const lockedHorses = (round.locked_horses || [])
                    .filter((lh) => lh.course_id === course.id)
                    .map((lh) => lh.horse || (course.horses || []).find((x) => x.id === lh.horse_id))
                    .filter(Boolean) as Horse[]
                  // Stable display order by horse number; place comes from selections
                  const displayHorses = [...lockedHorses].sort((a, b) => a.number - b.number)
                  return (
                    <section key={rc.id} className="lucky-horse__card">
                      <div className="lucky-horse__course-head">
                        <h3>{course.name}</h3>
                        <p>
                          {new Date(course.start_time).toLocaleString(numberLocale)}
                          {' · '}
                          {t('horse.orderHint')}
                        </p>
                      </div>
                      <p className="lucky-horse__hint">{t('horse.placeAmongHint')}</p>
                      <div className="lucky-horse__place-list">
                        {displayHorses.map((h) => {
                          const place = orderIds.indexOf(h.id) + 1
                          return (
                            <div key={h.id} className="lucky-horse__place-item">
                              <div className="lucky-horse__place-horse">
                                {h.photo_url && <img src={h.photo_url} alt="" className="lucky-horse__photo" />}
                                <strong>#{h.number}</strong>
                                <span>{h.name}</span>
                                {place > 0 && (
                                  <em>{t('horse.placeOption', { n: place })}</em>
                                )}
                              </div>
                              <div className="lucky-horse__place-row">
                                {[1, 2, 3, 4].map((n) => (
                                  <button
                                    key={n}
                                    type="button"
                                    className={`lucky-horse__place-btn ${place === n ? 'is-on' : ''}`}
                                    disabled={!!entry || !votingOpen}
                                    onClick={() => setThreeInFourPlace(course.id, h.id, n)}
                                  >
                                    {t('horse.placeOption', { n })}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </section>
                  )
                })}
              </>
            ) : (
              (round.courses || []).map((rc) => {
                const course = rc.course
                if (!course) return null
                const key = String(course.id)
                const picked = selections[key] || []
                return (
                  <section key={rc.id} className="lucky-horse__card">
                    <div className="lucky-horse__course-head">
                      <h3>{course.name}</h3>
                      <p>
                        {new Date(course.start_time).toLocaleString(numberLocale)}
                        {t('horse.pickNHorses', { n: need })}
                      </p>
                    </div>
                    <div className="lucky-horse__chips">
                      {(course.horses || []).map((h) =>
                        renderHorseChip(h, picked.includes(h.id), () => togglePick(course.id, h.id)),
                      )}
                    </div>
                  </section>
                )
              })
            )}

            {!entry && !votingOpen && (
              <p className="lucky-horse__hint text-amber-300 text-sm text-center">
                {t('horse.votingClosed')}
              </p>
            )}

            {!entry && votingOpen && feeBronze <= 0 && (
              <button
                type="button"
                className="lucky-horse__cta"
                disabled={!canSubmit || loading}
                onClick={() => handleSubmit('bronze')}
              >
                {t('horse.submitPoll')}
              </button>
            )}

            {!entry && votingOpen && feeBronze > 0 && (
              <div className="lucky-horse__pay">
                <button
                  type="button"
                  className="lucky-horse__cta"
                  disabled={!canSubmit || loading || watchingAd}
                  onClick={() => handleSubmit('bronze')}
                >
                  {t('horse.submitWithBronze', { n: feeBronze })}
                </button>
                {adsWatched < adsRequired ? (
                  <button
                    type="button"
                    className="lucky-horse__cta lucky-horse__cta--ad"
                    disabled={loading || watchingAd}
                    onClick={handleWatchAd}
                  >
                    {watchingAd
                      ? t('common.watchingAd')
                      : t('horse.watchVideos', { watched: adsWatched, required: adsRequired })}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="lucky-horse__cta lucky-horse__cta--ad"
                    disabled={!canSubmit || loading}
                    onClick={() => handleSubmit('ads')}
                  >
                    {t('horse.submitWithVideos', { n: adsRequired })}
                  </button>
                )}
              </div>
            )}

            {leaderboard.length > 0 && (
              <section className="lucky-horse__card">
                <h3 className="lucky-horse__section-title">
                  <LuckyIcon name="leaderboard" size={20} alt="" />
                  {t('horse.roundLeaderboard')}
                </h3>
                <div className="lucky-horse__board">
                  {leaderboard.map((e, i) => (
                    <div key={e.id} className="lucky-horse__board-row">
                      <span>
                        {i + 1}. {e.user?.first_name || `#${e.user_id}`}
                      </span>
                      <strong>{e.score}</strong>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
